import { useContext, useEffect, useRef, useState } from "react";
import TabbedPanel from "../../../components/TabbedPanel/TabbedPanel";
import "./LegacyCustomisation.scss";
import CustomisationControls from "../../../components/CustomisationControls/CustomisationControls";
import { IconJsxer } from "../../../helpers/IconHelper";
import { findFeatureData, fixSrcUrls, getPreviewCustomisationVars } from "../../../components/v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import ResetPresets from "../../CustomiseFeature/ResetPresets";
import PopUpPanel from "../../../components/PopUpPanel/PopUpPanel";
import AddDisruptor from "../../Campaigns/AddDisruptor";
import AddQuiz from "../../Campaigns/AddQuiz";
import axios from "../../../api/axios";
import { toast } from "react-toastify";
import { getCampaignFeatureVarsRoute, getGenericQuizzesRoute, getQuizzesByCampaignRoute, updateCampaignFeatureVarsRoute, addHashTagRoute, deleteHashTagRoute } from "../../../api/routes";
import FontSelectorPopup from "../../../components/forms/FontSelector/FontSelectorPopup";
import CommonFontPopup from "../../CustomiseFeature/CommonFontPopup";
import CustomFontsPopup from "../../CustomiseFeature/CustomFontsPopup";
import FeaturePreview, { postMessageToPreviewFrame } from "../../../components/FeaturePreview/FeaturePreview";
import FunctionUnavailableInPreviewPopup from "../../CustomiseFeature/themes/FunctionUnavailableInPreviewPopup";
import { LIVE_AUDIO_PREVIEW } from "../../../helpers/LivePreviews";
import { FEATURE_CONTROLS_TYPES, POST_MESSAGE_OUT_TYPES } from "../../../helpers/FeatureControlsHelper";
import { AuthContext } from "../../../contexts/AuthContext";
import { ACCOUNT_TYPES } from "../../../helpers/AccountTypes";

const LegacyCustomisation = (props) => {
    console.log('Legacy customisation props: ', props);

    const featureData = props.feature;
    const campaignData = props.campaignData;
    const campaignId = campaignData._id;
    const customisationVars = props.customisationVars;
    const featureId = featureData._id;
    const [forceUpdate, setForceUpdate] = useState(false);
    const forceUpdateNow = () => setForceUpdate((oldVal) => !oldVal);

    // ── Hashtag management ────────────────────────────────────────────────────
    const [showHashtagPanel, setShowHashtagPanel] = useState(false);
    const [hashtagCommands, setHashtagCommands] = useState(() => campaignData.hashtagCommands || []);
    const [newHashtagInputs, setNewHashtagInputs] = useState({});  // { inputKey: string }
    const [hashtagSaving, setHashtagSaving]       = useState({});  // { inputKey: bool }

    const getHashtagsForCommand = (command, param) => {
        const featureIds = new Set(
            [featureData._id?.toString?.(), featureData.feature._id?.toString?.()].filter(Boolean)
        );
        for (const entry of hashtagCommands) {
            const entryFid   = entry.featureID?.toString?.() || '';
            const entryParam = entry.param === undefined ? null : entry.param;
            const cmdParam   = param === undefined || param === '' ? null : param;
            if (entry.command === command && featureIds.has(entryFid) && entryParam == cmdParam) {
                return (entry.hashtags || [])
                    .map(h => (typeof h === 'string' ? h : h?.hashtag))
                    .filter(Boolean);
            }
        }
        return [];
    };

    const addHashtag = (command, param) => {
        const inputKey = `${command}_${param}`;
        const raw = (newHashtagInputs[inputKey] || '').trim();
        if (!raw) return;
        const hashtagValue = raw.startsWith('#') ? raw : `#${raw}`;

        setHashtagSaving(prev => ({ ...prev, [inputKey]: true }));
        axios.post(addHashTagRoute, {
            campaignID:       campaignId,
            featureID:        featureData._id,
            featureTemplateID: featureData.feature._id,
            command,
            hashtag: { hashtag: hashtagValue, cooldown: 0, restricted: false },
            param:   param === '' ? undefined : param,
            commandParam: param === '' ? undefined : param,
        }, { withCredentials: true })
        .then(res => {
            const updated = res.data?.campaignData?.hashtagCommands;
            if (updated) setHashtagCommands(updated);
            setNewHashtagInputs(prev => ({ ...prev, [inputKey]: '' }));
            toast.success('Hashtag added');
        })
        .catch(err => toast.error('Failed to add hashtag'))
        .finally(() => setHashtagSaving(prev => ({ ...prev, [inputKey]: false })));
    };

    const removeHashtag = (command, param, hashtagValue) => {
        axios.post(deleteHashTagRoute, {
            campaignID:       campaignId,
            featureID:        featureData._id,
            featureTemplateID: featureData.feature._id,
            command,
            hashtag: { hashtag: hashtagValue },
            param:   param === '' ? undefined : param,
        }, { withCredentials: true })
        .then(res => {
            const updated = res.data?.campaignData?.hashtagCommands;
            if (updated) {
                setHashtagCommands(updated);
            } else {
                setHashtagCommands(prev =>
                    prev.map(entry => entry.command !== command ? entry : {
                        ...entry,
                        hashtags: (entry.hashtags || []).filter(h =>
                            (typeof h === 'string' ? h : h?.hashtag) !== hashtagValue
                        ),
                    })
                );
            }
            toast.success('Hashtag removed');
        })
        .catch(() => toast.error('Failed to remove hashtag'));
    };
    // ─────────────────────────────────────────────────────────────────────────

    const [authContext, setAuthContext] = useContext(AuthContext);

    const [currentTab, setCurrentTab] = useState('visual');
    const tabData = useRef(null);
    const buildTabs = (campaignData) => {
        setForceUpdate(false);
        // const featureData = featureData; // findFeatureData(campaignData);
        console.log('Building tabs for feature: ', featureData);
        if (featureData) {
            const newTabData = [];
            for (let i = 0; i < featureData.feature.customisation.length; i++) {
                newTabData.push(
                    {
                        label: featureData.feature.customisation[i].tabTitle,
                        id: featureData.feature.customisation[i].tabKey,
                    }
                )
            }
            tabData.current = newTabData;
            setCurrentTab(newTabData[0].id);
        }
    }

    useEffect(() => {
        buildTabs(campaignData);
    }, []);

    const [featureVars, setFeatureVars] = useState({});
    const featureVarsRef = useRef(null);
    const getFeatureVars = () => {
        console.log('Get Campaign Feature Vars');
        axios
        .get(getCampaignFeatureVarsRoute, {
            params: { campaignID: campaignId },
            withCredentials:true
        })
        .then(function (response) {
            console.log('CAMPAIGN STREAM VARS',response.data);
            if (response.data.streamVars) {
                featureVarsRef.current = response.data.streamVars;
                setFeatureVars(featureVarsRef.current);
            }
            // getCustomisationVars();
        })
        .catch(function (error) {
            toast.error('Error - ' + error);
            // console.log(error.response)
        });
    }
    const updateFeatureVars = (updatedVars) => {
        axios
        .post(updateCampaignFeatureVarsRoute, {
            campaignID: campaignId, 
            updatedVars: updatedVars,
            withCredentials:true
        })
        .then(function (response) {
            console.log(response.data);
        })
        .catch(function (error) {
            toast.error('Error - ' + error);
        });
    }
    useEffect(getFeatureVars, [campaignId, featureId, /*streamId*/]);

    const [showLightbox, setShowLightbox] = useState(false);
    const [lightboxImgSrc, setLightboxImgSrc] = useState(null);
    const [imgDimensions, setImgDimensions] = useState(null);

    const imgRef = useRef(null);
    const zoomImage = (src) => {
        setLightboxImgSrc(src);
        setImgDimensions(null);
        setShowLightbox(true);

        imgRef.current = new Image();
        imgRef.current.onload = function() {
            setImgDimensions({x: this.width, y: this.height});
        }
        imgRef.current.src = src;
    }
    const closeLightBox = () => {
        setShowLightbox(false);
        imgRef.current = null;
    }

    const [showResetPresetsPanel, setShowResetPresetsPanel] = useState(false);
    const presetsData_ref = useRef(null);
    const [presetsData, setPresetsData] = useState(null);
    const presetVarsParent = useRef(null);
    const presetUpdatedCallback = useRef(null);
    const openResetPresetsPanel = (presetsData, varsParent, valsUpdatedCallback) => {
        presetsData_ref.current = presetsData;
        setPresetsData(presetsData);
        presetVarsParent.current = varsParent;
        presetUpdatedCallback.current = valsUpdatedCallback;
        setShowResetPresetsPanel(true);
    }
    const closeResetPresetsPanel = () => {
        setShowResetPresetsPanel(false);
    }
    const resetPresetsNow = (presetIndex) => {
        let presetsArray = presetsData_ref.current.vals;
        if (presetIndex >= 0) {
            presetsArray = presetsData_ref.current.presets[presetIndex].vals;
        }

        // first clear out the current vals array, but store it in case any vals in our preset are missing!
        const oldVals = [];
        while (presetVarsParent.current.length > 0) {
            oldVals.push(presetVarsParent.current.shift());
        }
        // now insert new preset vals
        for (let i = 0; i < presetsArray.length; i++) {
            presetVarsParent.current.push({...presetsArray[i]});
            // If a val doesn't exist in our preset keep the old value.
            // We do this so we can have partial presets where not every value is replaced...
            let valFixed = false;
            if (oldVals.length > i) {
                for (let prop in oldVals[i]) {
                    if (typeof presetVarsParent.current[i][prop] === 'undefined') {
                        presetVarsParent.current[i][prop] = oldVals[i][prop];
                        valFixed = true;
                    }
                }
            }
            // No val set? Let's try the default vals.
            if (!valFixed) {
                if (presetsData_ref.current.vals.length > i) {
                    const defaultVals = presetsData_ref.current.vals;
                    for (let prop in defaultVals[i]) {
                        if (typeof presetVarsParent.current[i][prop] === 'undefined') {
                            presetVarsParent.current[i][prop] = defaultVals[i][prop];
                            valFixed = true;
                        }
                    }
                }
            }
            // Or from our valSchema default settings..
            if (!valFixed) {
                const valSchema = presetsData_ref.current.valSchema;
                for (let prop in valSchema) {
                    if (typeof presetVarsParent.current[i][prop] === 'undefined' && typeof valSchema[prop].default !== 'undefined' && valSchema[prop.default !== null]) {
                        presetVarsParent.current[i][prop] = valSchema[prop].default;
                        valFixed = true;
                    }
                }
            }
        }

        fixSrcUrls(featureData);

        if (presetUpdatedCallback.current) {
            presetUpdatedCallback.current();
        }
    }

    // Adding disruptors
    const [showAddDistruptor, setShowAddDistruptor] = useState(false);
    const disruptorsUpdatedCallback = useRef(null);
    const existingDisruptorAddedCallback = useRef(null);

    const lastDisruptorAdded = useRef(null);
    const lastFeatureListUpdate = useRef(null);
    const [buildLeaderboard, setBuildLeaderboard] = useState(false);
    const [newDisruptorLabel, setNewDisruptorLabel] = useState(null);
    const [restrictedDisruptorType, setRestrictedDisruptorType] = useState(null);

    const addDisruptor = (completedCallback, addExistingCallback, isLeaderboard=false, disruptorLabel = null, disruptorType = null) => {
        if (disruptorLabel === '') {
            disruptorLabel = null;
        }
        if (disruptorType === 'any') {
            disruptorType = null;
        }

        disruptorsUpdatedCallback.current = completedCallback;
        existingDisruptorAddedCallback.current = addExistingCallback;

        lastDisruptorAdded.current = null;
        lastFeatureListUpdate.current = null;
        setBuildLeaderboard(isLeaderboard);
        setNewDisruptorLabel(disruptorLabel);
        setRestrictedDisruptorType(disruptorType);
        setShowAddDistruptor(true);
    };
    const closeAddDisruptor = () => {
        setShowAddDistruptor(false);
    };

    /********************************************************
     * ADD QUIZ FUNCTIONALITY
     *******************************************************/
    const [showAddQuiz, setShowAddQuiz] = useState(false);
    const closeAddQuiz = () => {setShowAddQuiz(false)};
    const quizUpdatedCallback = useRef(null);
    const existingQuizAddedCallback = useRef(null);
    const addQuiz = (completedCallback, addExistingCallback) => {
        //disruptorsUpdatedCallback.current = completedCallback;
        //existingDisruptorAddedCallback.current = addExistingCallback;

       // lastDisruptorAdded.current = null;
        //lastFeatureListUpdate.current = null;
        quizUpdatedCallback.current = completedCallback;
        existingQuizAddedCallback.current = addExistingCallback;
        setShowAddQuiz(true);
    };

    // Quiz data qill be needed here, but we don't always need to pull it in - it depends on the disruptor type we are customising
    const [campaignQuizzes, setCampaignQuizzes] = useState([]);
    const [genericQuizzes, setGenericQuizzes] = useState([]);
    
    const getCampaignQuizzes = () => {
        axios
            .get(getQuizzesByCampaignRoute, {params:{campaignID: campaignId}, withCredentials: true})
            .then((res) => {
                setCampaignQuizzes(res.data.quizzes);

                console.log('Campaign quizzes: ', res.data.quizzes);
            })
            .catch(err => {
                toast.error("Failed to get campaign quizzes")
            })
    };
    const getGenericQuizzes = () => {
        axios
            .get(getGenericQuizzesRoute, {withCredentials: true})
            .then((res) => {
                setGenericQuizzes(res.data.quizzes);

                console.log('Generic quizzes: ', res.data.quizzes);
            })
            .catch(err => {
                toast.error("Failed to get generic quizzes")
            })
    };

    useEffect(() => {
        // Do we need to pull in quiz data?
        if (featureData.feature.varKey === 'quizPoll') {
            getCampaignQuizzes();
            getGenericQuizzes();
        }
    }, [featureData.feature.varKey]);

    const [showFontSelector, setShowFontSelector] = useState(false);
    const fontSelectedFunc_ref = useRef(null);
    const currentFont_ref = useRef(null);
    const openFontSelector = (currentFont, selectedFunc) => {
        currentFont_ref.current = currentFont;
        fontSelectedFunc_ref.current = selectedFunc;
        setShowFontSelector(true);
    }
    const closeFontSelector = () => {
        setShowFontSelector(false);
    }

    const [showCustomFontsPopup, setShowCustomFontsPopup] = useState(false);
    const openCustomFontsPopup = () => {
        setShowCustomFontsPopup(true);
    }
    const closeCustomFontsPopup = () => {
        setShowCustomFontsPopup(false);
    }

    const [showCommonFontPopup, setShowCommonFontPopup] = useState(false);
    const addValueToThemeFunc_ref = useRef(null);
    const openCommonFontPopup = (addValueToThemeFunc = null) => {
        setShowCommonFontPopup(true);
        addValueToThemeFunc_ref.current = addValueToThemeFunc;
    }
    const closeCommonFontPopup = () => {
        setShowCommonFontPopup(false);
    }
    const setCommonFont = (font) => {
        const featureData = findFeatureData(campaignData, featureId);
        const isTheme = false; // showingEditTheme;
        const addToThemeFunc = addValueToThemeFunc_ref.current;
        var updated = false;
        // console.log('Set common font for feature to font: ', featureData, font);
        if (featureData && featureData.feature && featureData.feature.customisation) {
            // Tabs
            for (let i = 0; i < featureData.feature.customisation.length; i++) {
                // Tab Options
                for (let j = 0; j < featureData.feature.customisation[i].options.length; j++) {
                    // Variables
                    for (let k = 0; k < featureData.feature.customisation[i].options[j].variables.length; k++) {
                        for (let prop in featureData.feature.customisation[i].options[j].variables[k].valSchema) {
                            if (featureData.feature.customisation[i].options[j].variables[k].valSchema[prop].type === 'font') {
                                // This is a font we need to set
                                const tabKey = featureData.feature.customisation[i].tabKey;
                                const varKey = featureData.feature.customisation[i].options[j].varKey;
                                const varName = featureData.feature.customisation[i].options[j].variables[k].varName;
                                // console.log('Setting common font: ', font, tabKey, varKey, varName, prop, customisationVarsRef.current.current[featureId]);
                                for (let l = 0; l < customisationVars.current[featureId][tabKey][varKey][varName].length; l++) {
                                    if (!isTheme) {
                                        customisationVars.current[featureId][tabKey][varKey][varName][l][prop] = font;
                                    } else {
                                        if (addToThemeFunc) {
                                            // addValueToTheme = (tabKey, familyKey, varKey, varIndex, schemaKey, value, varSchema)

                                            addToThemeFunc(tabKey, varKey, varName, l, prop, font, featureData.feature.customisation[i].options[j].variables[k].valSchema[prop]);
                                        }
                                    }
                                    updated = true;
                                }
                            }
                        }
                    }
                }
            }
        }
        if (updated) {
            // setCustomisationVars({...customisationVarsRef.current});
            forceUpdateNow();
        }
    }

    const [previewMode, setPreviewMode] = useState(false);
    const [previewPostFix, setPreviewPostFix] = useState('');
    const [currentFeature, setCurrentFeature] = useState(null);
    const currentFeatureRef = useRef(null);
    const [previewUpdate, setPreviewUpdate] = useState(0);
    const previewThisCustomisation = () => {
        // const featureData = findFeatureData(campaignData);
        editFeatureSettings(featureData, "(Customising)");
    }
    const editFeatureSettings = (f, postFix = "") => {
        currentFeatureRef.current = f;
        setPreviewPostFix(postFix);
        setCurrentFeature(f);
        setPreviewMode(true);
    }

    const previewUpdateTimeoutId = useRef(null);
    const refreshPreview = () => {
        if (previewMode && currentFeatureRef.current) {
            reportFeatureVarsChanged(true);
            reportCustomisationVarsChanged(customisationVars, currentFeatureRef.current._id);
        }

        if (previewMode) {
            clearTimeout((previewUpdateTimeoutId.current));
            previewUpdateTimeoutId.current = setTimeout(
                () => setPreviewUpdate((currentVal) => currentVal + 1),
                500
            );
        }
    }

    const getCurrentFeatureVars = () => featureVarsRef.current?.current || featureVarsRef.current;

    const reportFeatureVarsChanged = (onlyUpdateDisruptor = false) => {
        const currentFeatureVars = getCurrentFeatureVars();
        const currentPreviewFeatureId = currentFeatureRef.current?._id;

        if (!currentFeatureVars || !currentPreviewFeatureId) {
            return;
        }

        if (!onlyUpdateDisruptor) {
            updateFeatureVars(currentFeatureVars);
        }

        postMessageToPreviewFrame(
            POST_MESSAGE_OUT_TYPES.SETTINGS_VARS_RECEIVED,
            currentFeatureVars[currentPreviewFeatureId]
        );
    }

    const reportCustomisationVarsChanged = (updatedVars, updatedFeatureId) => {
        const currentPreviewFeature = currentFeatureRef.current;
        const currentCustomisationVars = updatedVars?.current || updatedVars;

        if (!currentPreviewFeature || !updatedFeatureId || !currentCustomisationVars?.[updatedFeatureId]) {
            return;
        }

        postMessageToPreviewFrame(
            POST_MESSAGE_OUT_TYPES.CUSTOMISATION_VARS_RECEIVED,
            getPreviewCustomisationVars(
                currentPreviewFeature,
                updatedFeatureId,
                campaignId,
                currentCustomisationVars[updatedFeatureId]
            )
        );
    }

    const [showingFunctionUnavilablePopup, setShowingFunctionUnavilablePopup] = useState(false);
    const openFunctionUnavilablePopup = () => {
        setShowingFunctionUnavilablePopup(true);
    }
    const closeFunctionUnavilablePopup = () => {
        setShowingFunctionUnavilablePopup(false);
    }

    // Special functions that can be triggered by buttons in the feature controls in place of the normal feature commands...
    const BUTTON_FUNCTIONS = {
        reportFeatureVarsChanged: () => reportFeatureVarsChanged(),
        startLivePoll: (featureId, param) => {
            //reportFeatureVarsChanged();
            setTimeout(() => {
                sendFeatureCommand(null, 'START_POLL', featureId, param);
                // postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: 'START_POLL', extraData: {username:'CampaignStudio', hashtag:'Manual Press', commandParam: param} })
            }, 100);
        },
        createNewLivePoll: (featureId, param) => {
            // openRestrictedCustomisationPopup(featureId);
            openFunctionUnavilablePopup();
        },
        editRandomizerOptions: (featureId, param) => {
            openFunctionUnavilablePopup();
        },
        editLiveTextPresets: (featureId, param) => {
            // openRestrictedCustomisationPopup(featureId);
            openFunctionUnavilablePopup();
        },
        saveLiveTextPreset: (featureId, param) => {
            // openRestrictedCustomisationPopup(featureId);
            openFunctionUnavilablePopup();
        },
        saveResponseLiveTextPreset: (featureId, param) => {
            // openRestrictedCustomisationPopup(featureId);
            openFunctionUnavilablePopup();
        },
        selectLiveTextPreset: (featureId, param) => {
            const _featureVars = featureVarsRef.current.current[featureId];
            const _customisationVars = customisationVars.current.current[featureId];
            console.log('Select live text preset: ', featureId, _featureVars, _customisationVars);

            const presetTextIndex = Number(_featureVars.liveTextControls.presetText);
            const presetTextData = _customisationVars.setup.presetText.presets[presetTextIndex];
            if (presetTextData) {
                _featureVars.liveTextControls.presetName = presetTextData.presetName;
                _featureVars.liveTextControls.headerText = presetTextData.headerText;
                _featureVars.liveTextControls.mainContent = presetTextData.mainContent;
                featureVarsRef.current = {current: {...featureVarsRef.current.current, [featureId]: _featureVars}};
                setFeatureVars(featureVarsRef.current);
                // reportFeatureVarsChanged();
            }
        },
        selectResponseLiveTextPreset: (featureId, param) => {
            const _featureVars = featureVarsRef.current.current[featureId];
            const _customisationVars = customisationVars.current.current[featureId];
            console.log('Select live text preset: ', featureId, _featureVars, _customisationVars);

            const presetTextIndex = Number(_featureVars.responseTextControls.presetText);
            const presetTextData = _customisationVars.setup.presetText.presets[presetTextIndex];
            if (presetTextData) {
                _featureVars.responseTextControls.presetName = presetTextData.presetName;
                _featureVars.responseTextControls.headerText = presetTextData.headerText;
                _featureVars.responseTextControls.mainContent = presetTextData.mainContent;
                featureVarsRef.current = {current: {...featureVarsRef.current.current, [featureId]: _featureVars}};
                setFeatureVars(featureVarsRef.current);
                // reportFeatureVarsChanged();
            }
        },
        previewAudio: (featureId, param, frameId) => {
            // console.log('Preview audio: ', featureId, param);
            // Find the srcUrl in the param object. This will be inside another property, which could be called anything.
            let srcUrl = null;
            for (const key in param) {
                if (param[key].srcUrl) {
                    srcUrl = param[key].srcUrl;
                    break;
                }
            }
            if (!srcUrl) {
                console.log('No srcUrl found in param: ', param);
                return;
            }
            LIVE_AUDIO_PREVIEW.playStopAudio(srcUrl);
        },
    }
    const sendFeatureCommand = (e, commandString, featureId, param = null) => {
        if (e) {
            e.preventDefault();
        }
        if (BUTTON_FUNCTIONS[commandString]) {
            console.log('Running button function: ', commandString, featureId, param);
            BUTTON_FUNCTIONS[commandString](featureId, param);
        } else {
            postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: commandString, extraData: {username:'CampaignStudio', hashtag:'Manual Press', commandParam: param} });
        }
    }

    const pCountRef = useRef(1);
    const exportPreset = () => {
        // Function to download data to a file
        const download = (data, filename, type) => {
            var file = new Blob([data], {type: type});
            if (window.navigator.msSaveOrOpenBlob) // IE10+
                window.navigator.msSaveOrOpenBlob(file, filename);
            else { // Others
                var a = document.createElement("a"),
                        url = URL.createObjectURL(file);
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                setTimeout(function() {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);  
                }, 0); 
            }
        }

        const dataSchema = findFeatureData(campaignData, featureId).feature.customisation;
        console.log('Data schema: ', dataSchema);
        // Rebuild the data object save as a file...
        const rebuiltDataStructure = {};
        for (let i = 0; i < dataSchema.length; i++) {
            // Tab groups
            const tabKey = dataSchema[i].tabKey;
            for (let j = 0; j < dataSchema[i].options.length; j++) {
                // Tab group options
                const varKey = dataSchema[i].options[j].varKey;
                for (let k = 0; k < dataSchema[i].options[j].variables.length; k++) {
                    const varName = dataSchema[i].options[j].variables[k].varName;
                    // We now have all the keys to build our data structure
                    if (rebuiltDataStructure[tabKey] === undefined) {
                        rebuiltDataStructure[tabKey] = {};
                    }
                    if (rebuiltDataStructure[tabKey][varKey] === undefined) {
                        rebuiltDataStructure[tabKey][varKey] = {};
                    }
                    if (rebuiltDataStructure[tabKey][varKey][varName] === undefined) {
                        rebuiltDataStructure[tabKey][varKey][varName] = [];
                    }
                    // Drop any invalid data (maybe they forgot to select a file)...
                    // console.log('--- SAVE DATA VALIDATION ---');
                    /* while (customisationVarsRef.current.current[featureId][tabKey][varKey][varName].length > 0 && validateLastVal(customisationVarsRef.current.current[featureId][tabKey][varKey][varName], dataSchema[i].options[j].variables[k].valSchema) === false) {
                        console.log('--- REMOVED!!! ---');
                        customisationVarsRef.current.current[featureId][tabKey][varKey][varName].pop();
                    } */
                    // While loop and pop doesn't do the trick - what if I add 3 values and the middle one is valid, the last one will be removed, but the other invalid one won't
                    
                    // Commented this out as it's stopping me exporting a full data set for developing the claw feature
                    /* for (let validate_i = customisationVarsRef.current.current[featureId][tabKey][varKey][varName].length - 1; validate_i >= 0; validate_i--) {
                        if (validateValAtIndex(customisationVarsRef.current.current[featureId][tabKey][varKey][varName], dataSchema[i].options[j].variables[k].valSchema, validate_i) === false) {
                            // console.log('--- Validation (removed): ', tabKey, varKey, varName, validate_i, JSON.parse(JSON.stringify(customisationVarsRef.current.current[featureId][tabKey][varKey][varName])), JSON.parse(JSON.stringify(dataSchema[i].options[j].variables[k].valSchema)));
                            customisationVarsRef.current.current[featureId][tabKey][varKey][varName].splice(validate_i, 1);
                        }
                    } */
                    
                    // Now add in our new data, being careful to only send updated vars and preserve isDefault
                    for (let l = 0; l < customisationVars.current[featureId][tabKey][varKey][varName].length; l++) {
                        // if (customisationVarsRef.current.current[featureId][tabKey][varKey][varName][l].updated) {
                            const newDataObj = {};
                            for (let prop in dataSchema[i].options[j].variables[k].valSchema) {
                                // Don't add any data that for controls marked as isCosmetic (helps to cut down on data we don't need to save)
                                let includeData = true;
                                const dataType = dataSchema[i].options[j].variables[k].valSchema[prop].type;
                                for (let typeProp in FEATURE_CONTROLS_TYPES) {
                                    if (FEATURE_CONTROLS_TYPES[typeProp].type.toLowerCase() === dataType.toLowerCase()) {
                                        if (FEATURE_CONTROLS_TYPES[typeProp].isCosmetic) {
                                            includeData = false;
                                        }
                                        break;
                                    }
                                }
                                if (includeData) {
                                    newDataObj[prop] = customisationVars.current[featureId][tabKey][varKey][varName][l][prop];
                                }
                            }
                            newDataObj.isDefault = customisationVars.current[featureId][tabKey][varKey][varName][l]["isDefault"];
                            rebuiltDataStructure[tabKey][varKey][varName].push(newDataObj);
                        // }
                    }
                }
            }
        }
        console.log('Built data to send: ', rebuiltDataStructure);

        const dataToSend = {};
        dataToSend[featureId] = rebuiltDataStructure;

        const data = JSON.stringify(dataToSend[featureId], null, 2);
        download(data, featureData.feature.featureName + '_preset_' + pCountRef.current + '.json', 'text/plain');
        pCountRef.current++;
    }

    const [saving, setSaving] = useState(false);
    const saveComplete = () => {
        setSaving(false);
    };

    return (
        <div className="v2-legacy-customisation-holder">
            <div className="fl-column">
                <div className="fl-row grow">
                    <h4 className="contentLabel">{props.feature.contentLabel} (Legacy Customisation)</h4>
                    
                    <div className="grow"></div>
                    <div className="grow"></div>
                    <div className="grow"></div>
                    <div className="grow"></div>
                    <div className="grow"></div>
                    <div className="grow"></div>
                    <div className="grow"></div>
                    <div className="grow"></div>

                    { authContext.userData.userLevel >= ACCOUNT_TYPES.koko.level &&
                        <div className="main-top-options" onClick={exportPreset}>Export Preset {IconJsxer.GetIcon(IconJsxer.ICONS.undelete, IconJsxer.ICON_STYLES.customiseTopOptions)}</div>
                    }

                    <div className="main-top-options" onClick={openCustomFontsPopup}>Custom Fonts {IconJsxer.GetIcon(IconJsxer.ICONS.font, IconJsxer.ICON_STYLES.customiseTopOptions)}</div>
                    <div className="main-top-options" onClick={openCommonFontPopup}>Set Common Font {IconJsxer.GetIcon(IconJsxer.ICONS.font, IconJsxer.ICON_STYLES.customiseTopOptions)}</div>
                    <div className="main-top-options" onClick={previewThisCustomisation}>Preview {IconJsxer.GetIcon(IconJsxer.ICONS.preview, IconJsxer.ICON_STYLES.customiseTopOptions)}</div>
                    {featureData.feature.commands?.some(c => c.isHashtagEnabled) &&
                        <div className="main-top-options" onClick={() => setShowHashtagPanel(true)}>Hashtags {IconJsxer.GetIcon(IconJsxer.ICONS.hashtag, IconJsxer.ICON_STYLES.customiseTopOptions)}</div>
                    }
                    
                    <div className="grow"></div>

                    {saving && <button className="save-button disabled" onClick={null}>Saving...</button>}
                    {!saving &&
                        <button className="save-button" onClick={() => { setSaving(true); props.saveFunction(saveComplete); }}>Save Changes</button>
                    }
                    <div className="close-button" onClick={props.onClose}>Close</div>
                </div>
                {tabData.current && tabData.current.length > 0 &&
                    <div className="fl-row grow">
                        <div className="fl-column grow">
                            <TabbedPanel
                                tabs={
                                    /* POTENTIAL_TABS */
                                    tabData.current
                                }
                                switchTabCallback={(tabId, tabData) => setCurrentTab(tabData.id)}
                                initialTab={0}
                                forceUpdate={forceUpdate}
                                tabWidth={tabData.current.length < 7 ? '20%' : 100}
                                tabStyle={2}
                            >

                                <div className="tab-content">
                                    <div className="co-tab-content">

                                        {customisationVars.current && customisationVars.current[featureId] &&
                                            <CustomisationControls
                                                tabId={currentTab}
                                                controlsData={featureData.feature.customisation}
                                                customisationVars={customisationVars}
                                                featureId={featureId}
                                                featureData={featureData}
                                                campaignData={campaignData}
                                                handleRemovedFile={() => {} /* handleRemovedFile */}
                                                zoomFunction={zoomImage}
                                                showTooltipFunc={props.showTooltipFunc}
                                                hideTooltipFunc={props.hideTooltipFunc}
                                                openResetPresetsPanel={openResetPresetsPanel}
                                                addDisruptor={addDisruptor}
                                                addQuiz={addQuiz}
                                                customiseQuiz={() => {} /* customiseQuiz */}
                                                logAddedDisruptor={() => {} /* logAddedDisruptor */}
                                                logRemovedDisruptor={() => {} /* logRemovedDisruptor */}
                                                showFeaturePreview={null /* showFeaturePreview */}
                                                customiseFeature={null /* customiseFeature */}
                                                refreshPreview={refreshPreview}
                                                openFontSelectorFunc={openFontSelector}
                                                advancedMode={true}
                                                forceVarStyle = {{
                                                    "addBox": false,
                                                    "addOutlineBox": true,
                                                    "boxColour": "#EFF0F6",
                                                    "addVarBox": false,
                                                    "addVarOutlineBox": false,
                                                    "varBoxColour": "#EFF0F6",
                                                }}
                                            />
                                        }

                                    </div>
                                </div>

                            </TabbedPanel>
                        </div>
                    </div>
                }
            </div>

            {showLightbox &&
                <div className="blackout lightbox">
                    <img src={lightboxImgSrc} alt="" />
                    <div className="fl-row">
                        <div className="grow"></div>
                        {imgDimensions &&
                            <div>{imgDimensions.x} x {imgDimensions.y}</div>
                        }
                        <div className="grow"></div>
                    </div>
                    <div className="close-btn" onClick={closeLightBox}>
                        {IconJsxer.GetIcon(IconJsxer.ICONS.closeX, IconJsxer.ICON_STYLES.roundPanelButton)}
                    </div>
                </div>
            }
            {showResetPresetsPanel &&
                <ResetPresets
                    closePanelFunc={closeResetPresetsPanel}
                    label={presetsData.label}
                    presets={presetsData.presets}
                    resetPresetFunc={resetPresetsNow}
                />
            }
            {showAddDistruptor &&
                <AddDisruptor
                    closePanelFunc={closeAddDisruptor} 
                    campaignId={campaignId} 
                    addSelectedFeatureToListFunc={() => {} /* addSelectedFeatureToList */}
                    addExistingDisruptorToListFunc={existingDisruptorAddedCallback.current}
                    campaignTier={campaignData.campaignTier}
                    isIntro={false}
                    isOutro={false}
                    chainableOnly={true}
                    showSelectExistingOption={true}
                    isRestricted={true} // isRestricted stops us adding new distruptors, we only want to choose from existing ones
                    campaignFeatures={campaignData.features}
                    buildLeaderboard={buildLeaderboard}
                    disruptorContentLabel={newDisruptorLabel}
                    restrictTypeTo={restrictedDisruptorType}
                />
            }
            {showAddQuiz &&
                <AddQuiz
                    closePanelFunc={closeAddQuiz} 
                    campaignId={campaignId} 
                    showSelectExistingOption={true}

                    genericQuizzes={genericQuizzes}
                    campaignQuizzes={campaignQuizzes}
        
                    addExistingQuizToList={existingQuizAddedCallback.current}
                    />
            }
            {showFontSelector &&
                <FontSelectorPopup
                    closePanelFunc={closeFontSelector}
                    selectFunc={fontSelectedFunc_ref.current}
                    font={currentFont_ref.current}
                    customFonts={campaignData.customFonts}
                    campaignId={campaignId}
                />
            }
            {showCommonFontPopup &&
                <CommonFontPopup
                    closePanelFunc={closeCommonFontPopup}
                    setCommonFontFunc={setCommonFont}
                    editingTheme={false/*showingEditTheme*/}
                    customFonts={campaignData.customFonts}
                    campaignId={campaignId}
                />
            }
            {showCustomFontsPopup &&
                <CustomFontsPopup
                    customFonts={campaignData.customFonts}
                    campaignId={campaignId}
                    fullCampaignData={campaignData}
                    closePanelFunc={closeCustomFontsPopup}
                />
            }
            {previewMode &&
                <FeaturePreview
                    campaignId={campaignId}

                    currentFeature={currentFeature}
                    titlePostFix={previewPostFix}
                    featureVars={featureVars}
                    streamId={null/*streamId*/}
                    customisationVars={customisationVars}
                    closeFunc={() => setPreviewMode(false)}

                    startControlsExpanded={true/*currentFeature.feature.featureType === 'feature'*/}
                    startMaximized={false}
                    resizeable={true}
                    startPosX={'right'}
                    showQuickPlayButton={true}
                    autoStart={true}
                    previewUpdate={previewUpdate}

                    _reportFeatureVarsChanged={reportFeatureVarsChanged}
                    _reportCustomisationVarsChanged={reportCustomisationVarsChanged}
                    
                    _sendFeatureCommandFunc={sendFeatureCommand}
                    _showTooltip={props.showTooltipFunc}
                    _hideTooltip={props.hideTooltipFunc}
                />
            }

            {showingFunctionUnavilablePopup &&
                <FunctionUnavailableInPreviewPopup
                    closePanelFunc={closeFunctionUnavilablePopup}
                />
            }

            {showHashtagPanel &&
                <PopUpPanel closePanelFunc={() => setShowHashtagPanel(false)} className="legacy-hashtag-panel">
                        <h2>Chat Commands (Hashtags)</h2>
                        <div className="legacy-hashtag-panel-body">
                            {featureData.feature.commands.filter(c => c.isHashtagEnabled).length === 0
                                ? <p className="legacy-hashtag-empty-msg">This feature has no hashtag-enabled commands.</p>
                                : featureData.feature.commands.filter(c => c.isHashtagEnabled).map((cmd, idx) => {
                                    const inputKey = `${cmd.command}_${cmd.param ?? ''}`;
                                    const tags = getHashtagsForCommand(cmd.command, cmd.param);
                                    return (
                                        <div key={idx} className="legacy-hashtag-command-block">
                                            <div className="legacy-hashtag-command-label">{cmd.title || cmd.command}</div>
                                            <div className="legacy-hashtag-tags fl-row">
                                                {tags.length === 0
                                                    ? <span className="legacy-hashtag-none">No hashtags set</span>
                                                    : tags.map((tag, ti) => (
                                                        <div key={ti} className="legacy-hashtag-tag fl-row">
                                                            <span>{tag}</span>
                                                            <button
                                                                className="legacy-hashtag-tag-remove"
                                                                title="Remove hashtag"
                                                                onClick={() => removeHashtag(cmd.command, cmd.param ?? '', tag)}
                                                            >
                                                                {IconJsxer.GetIcon(IconJsxer.ICONS.closeX, IconJsxer.ICON_STYLES.standardNeutral)}
                                                            </button>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                            <div className="legacy-hashtag-add-row fl-row">
                                                <input
                                                    type="text"
                                                    className="legacy-hashtag-input"
                                                    placeholder="#hashtag"
                                                    value={newHashtagInputs[inputKey] || ''}
                                                    onChange={e => setNewHashtagInputs(prev => ({ ...prev, [inputKey]: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') addHashtag(cmd.command, cmd.param ?? ''); }}
                                                />
                                                <button
                                                    className="legacy-hashtag-add-btn"
                                                    disabled={!!hashtagSaving[inputKey] || !(newHashtagInputs[inputKey] || '').trim()}
                                                    onClick={() => addHashtag(cmd.command, cmd.param ?? '')}
                                                >
                                                    {hashtagSaving[inputKey] ? '…' : 'Add'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            }
                        </div>
                </PopUpPanel>
            }
        </div>
    );
}

export default LegacyCustomisation;
